import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, Building2, User, PackageSearch, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "product", label: "Physical product" },
  { value: "service", label: "Service / repair" },
  { value: "bulk_supply", label: "Bulk supply / wholesale" },
  { value: "professional", label: "Skilled professional" },
  { value: "other", label: "Something else" },
];

const INITIAL = {
  requester_type: "customer",
  name: "",
  organization: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  category: "product",
  subject: "",
  description: "",
  quantity: "",
  budget_inr: "",
  needed_by: "",
};

export default function RequestSection() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.subject || !form.description) {
      toast.error("Please fill name, phone, subject and description.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/requests", payload);
      setSubmitted(true);
      setForm(INITIAL);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="request" data-testid="request-section" className="relative overflow-hidden rounded-3xl border border-foreground/10 bg-gradient-to-br from-brand-yellow/20 via-white to-brand-cyan/15 p-6 sm:p-10 lg:p-14">
      <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-brand-orange/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-brand-cyan/20 blur-3xl" />

      <div className="relative grid gap-8 lg:grid-cols-5">
        {/* Left copy */}
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-orange/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-brand-orange">
            <Sparkles size={12} /> Can't find it here?
          </div>
          <h2 className="mt-3 font-heading text-3xl font-extrabold leading-[1.05] tracking-tight sm:text-4xl">
            Tell us what you need.<br />
            <span className="text-brand-orange">We'll source it for you.</span>
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
            Individual buyer or enterprise procurement — request any product, service,
            professional or bulk supply that isn't listed. Our team responds within
            <b className="text-foreground"> 48 hours</b> with sourced options and pricing.
          </p>

          <ul className="mt-6 space-y-2 text-sm">
            {[
              "Verified suppliers across 28 states",
              "Custom quotes for volume & recurring orders",
              "SMS confirmation the moment you submit",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-brand-orange" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right form */}
        <div className="lg:col-span-3">
          {submitted ? (
            <div
              className="flex h-full flex-col items-center justify-center rounded-2xl border border-brand-orange/30 bg-white/70 p-10 text-center backdrop-blur"
              data-testid="request-success"
            >
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-orange text-white">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="mt-4 font-heading text-2xl font-bold">Request received!</h3>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                Thank you — a confirmation SMS is on its way. Our sourcing team will
                reach out within 48 hours with matching options.
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                className="mt-6 rounded-full bg-brand-ink text-white hover:bg-brand-ink/90"
                data-testid="new-request-btn"
              >
                Submit another request
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-foreground/10 bg-white p-5 shadow-sm sm:p-6" data-testid="request-form">
              {/* Requester type toggle */}
              <div className="mb-4 grid grid-cols-2 gap-2 rounded-xl bg-muted p-1">
                {[
                  { v: "customer", label: "I'm a customer", icon: User },
                  { v: "enterprise", label: "I represent a business", icon: Building2 },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, requester_type: opt.v }))}
                    data-testid={`requester-${opt.v}`}
                    className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${
                      form.requester_type === opt.v ? "bg-white shadow" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <opt.icon size={14} /> {opt.label}
                  </button>
                ))}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Full name *">
                  <Input value={form.name} onChange={set("name")} placeholder="Priya Sharma" required data-testid="req-name" />
                </Field>
                {form.requester_type === "enterprise" && (
                  <Field label="Company / organization">
                    <Input value={form.organization} onChange={set("organization")} placeholder="Acme Retail Pvt Ltd" data-testid="req-org" />
                  </Field>
                )}
                <Field label="Phone *">
                  <Input value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" required data-testid="req-phone" />
                </Field>
                <Field label="Email">
                  <Input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="req-email" />
                </Field>
                <Field label="City">
                  <Input value={form.city} onChange={set("city")} placeholder="Bengaluru" data-testid="req-city" />
                </Field>
                <Field label="State">
                  <Input value={form.state} onChange={set("state")} placeholder="Karnataka" data-testid="req-state" />
                </Field>
              </div>

              <div className="mt-4">
                <Field label="What are you looking for?">
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, category: c.value }))}
                        data-testid={`cat-${c.value}`}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                          form.category === c.value
                            ? "border-brand-orange bg-brand-orange text-white"
                            : "border-foreground/15 bg-white text-muted-foreground hover:border-brand-orange/40 hover:text-foreground"
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>

              <div className="mt-4 grid gap-3">
                <Field label="Subject *">
                  <Input value={form.subject} onChange={set("subject")} placeholder="e.g. Solar water pump 3HP for our farm" required data-testid="req-subject" />
                </Field>
                <Field label="Describe what you need *">
                  <Textarea
                    value={form.description}
                    onChange={set("description")}
                    placeholder="Specs, use-case, timeline, delivery location — anything that helps us source the right option."
                    rows={4}
                    required
                    data-testid="req-desc"
                  />
                </Field>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <Field label="Quantity / scale">
                  <Input value={form.quantity} onChange={set("quantity")} placeholder="e.g. 50 units" data-testid="req-qty" />
                </Field>
                <Field label="Budget (INR)">
                  <Input value={form.budget_inr} onChange={set("budget_inr")} placeholder="e.g. 2,00,000" data-testid="req-budget" />
                </Field>
                <Field label="Needed by">
                  <Input value={form.needed_by} onChange={set("needed_by")} placeholder="e.g. 15 Mar" data-testid="req-neededby" />
                </Field>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <PackageSearch size={12} className="text-brand-orange" />
                  We reply within 48 hours over SMS + call.
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-brand-orange px-6 text-white hover:bg-brand-orange/90"
                  data-testid="req-submit"
                >
                  <Send size={14} className="mr-1.5" />
                  {submitting ? "Submitting..." : "Send request"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
